import { Box, Typography } from '@mui/material'
import { Table } from 'antd'
import React from 'react'

import { numberFormat } from '../../../../../utils'
import { IMerchant } from '../../../../../types/merchant'
import {
  IByDistributionFilterMapped,
} from './DailySalesByDistributionReportFilter'
import { useUser } from '../../../../../hooks/useUser'

export interface IByDistributionReportRow {
  distributionObjectId: string | null
  distributionName: string | null
  count: number
  unit: number
  codTotal: number
  codCount: number
  bankTotal: number
  bankCount: number
  costTotal: number
  commission: number
  commissionOrder: number
  commissionPercent: number
  total: number
}

export interface IDailySalesByDistributionTableProps {
  data?: IByDistributionReportRow[]
  total?: number
  isLoading: boolean
  merchant?: IMerchant
  filter: IByDistributionFilterMapped
  onPageChange?: (page: number, perPage: number) => void
}

const DEFAULT_COMMISSION_TYPE = 'commission'
const DEFAULT_EXPENSE: number = 1
const DEFAULT_CAL_TAX: string = '0'
const DEFAULT_COD_PERCENT: number = 0
const DEFAULT_SHIPPING_COST: number = 1

const DailySalesByDistributionReportTable = (
  props: IDailySalesByDistributionTableProps,
) => {
  const {
    data = [],
    total = 0,
    merchant,
    filter,
    onPageChange,
  } = props
  const page = filter.page ?? 1
  const perPage = filter.perPage ?? 15
  const commissionTypeName = DEFAULT_COMMISSION_TYPE
  const expense = DEFAULT_EXPENSE
  const calTax = DEFAULT_CAL_TAX
  const codPercent = DEFAULT_COD_PERCENT
  const shippingCost = DEFAULT_SHIPPING_COST
  const costAmountTotal = 0

  const { user } = useUser()

  const reportsArray = data.map((row) => ({
    ...row,
    costAmountTotal,
  }))

  const totalSumOrder = reportsArray.reduce(
    (acc, item) => acc + (Number(item.count) || 0),
    0,
  )
  const totalSumUnit = reportsArray.reduce(
    (acc, item) => acc + (Number(item.unit) || 0),
    0,
  )
  const totalSumCodTotal = reportsArray.reduce(
    (acc, item) => acc + (Number(item.codTotal) || 0),
    0,
  )
  const totalSumCodCount = reportsArray.reduce(
    (acc, item) => acc + (Number(item.codCount) || 0),
    0,
  )
  const totalSumBankTotal = reportsArray.reduce(
    (acc, item) => acc + (Number(item.bankTotal) || 0),
    0,
  )
  const totalSumBankCount = reportsArray.reduce(
    (acc, item) => acc + (Number(item.bankCount) || 0),
    0,
  )
  const totalSumCostTotal = reportsArray.reduce(
    (acc, item) => acc + (Number(item.costTotal) || 0),
    0,
  )
  const totalSumCommission = reportsArray.reduce((acc, item) => {
    const comission = item?.[commissionTypeName as keyof IByDistributionReportRow] ?? 0
    return acc + (Number(comission) || 0)
  }, 0)
  const totalSumSubTotal = totalSumCodTotal + totalSumBankTotal

  const totalSumTaxAndReturn = reportsArray.reduce(
    (acc, item) =>
      acc +
      (Number(item.total) || 0) * ((merchant?.config?.taxPercent || 0) / 100) +
      (Number(item.codTotal) || 0) *
      ((merchant?.config?.returnPercent || 0) / 100),
    0,
  )
  const totalSumPercentCod = reportsArray.reduce(
    (acc, item) =>
      acc +
      (Number(item.codTotal) || 0) *
      ((merchant?.config?.codPercent || 0) / 100),
    0,
  )
  const totalSumShippingCostFix = reportsArray.reduce(
    (acc, item) =>
      acc +
      (Number(item.count) || 0) * (merchant?.config?.shippingCostFix || 0),
    0,
  )

  let totalSumCost =
    totalSumCostTotal +
    (expense === 1 ? reportsArray.length * costAmountTotal : 0) +
    totalSumCommission
  if (calTax === '1') totalSumCost += totalSumTaxAndReturn
  if (codPercent === 1) totalSumCost += totalSumPercentCod
  if (shippingCost === 1) totalSumCost += totalSumShippingCostFix

  const totalSumTotalAll = totalSumSubTotal - totalSumCost

  const getIncome = (row: IByDistributionReportRow) =>
    (row.bankTotal || 0) + (row.codTotal || 0)

  const getExpense = (row: IByDistributionReportRow) => {
    const taxAndReturnCost =
      ((row.total || 0) * (merchant?.config?.taxPercent || 0)) / 100 +
      ((merchant?.config?.returnPercent || 0) * (row.codTotal || 0)) / 100
    const codPercentInRow =
      ((merchant?.config?.codPercent || 0) * (row.codTotal || 0)) / 100
    const shippingCostInRow =
      shippingCost === 1
        ? (row.count || 0) * (merchant?.config?.shippingCostFix || 0)
        : 0
    const commission =
      Number(row?.[commissionTypeName as keyof IByDistributionReportRow] ?? 0) || 0
    let amount =
      (row.costTotal || 0) +
      commission +
      (expense === 1 ? costAmountTotal : 0) +
      shippingCostInRow
    if (calTax === '1') amount += taxAndReturnCost
    if (codPercent === 1) amount += Math.abs(codPercentInRow)
    return amount
  }

  const getBalance = (row: IByDistributionReportRow) => {
    const income = getIncome(row)
    const expenseAmount = getExpense(row)
    return income - expenseAmount
  }

  const columns: any[] = [
    {
      title: 'ชื่อเพจ',
      dataIndex: 'distributionName',
      key: 'distributionName',
      width: 140,
      align: 'left' as const,
      render: (name: string | null, row: IByDistributionReportRow) => (
        <Typography variant="subtitle2" color="text.secondary">
          {name ?? '-'}
        </Typography>
      ),
    },
    {
      title: 'จำนวนสั่งซื้อ',
      dataIndex: 'count',
      key: 'count',
      width: 100,
      align: 'center' as const,
      render: (count: number) => (
        <Typography variant="subtitle2" color="primary" textAlign="center">
          {count != null
            ? count.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'จำนวนชิ้น',
      dataIndex: 'unit',
      key: 'unit',
      width: 90,
      align: 'center' as const,
      render: (unit: number) => (
        <Typography variant="subtitle2" color="text.secondary" textAlign="center">
          {unit != null
            ? unit.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'ยอด COD',
      dataIndex: 'codTotal',
      key: 'codTotal',
      width: 100,
      align: 'center' as const,
      render: (codTotal: number) => (
        <Typography variant="subtitle2" color="green" textAlign="center">
          {codTotal != null
            ? codTotal.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'จำนวน COD',
      dataIndex: 'codCount',
      key: 'codCount',
      width: 95,
      align: 'center' as const,
      render: (codCount: number) => (
        <Typography variant="subtitle2" color="text.secondary" textAlign="center">
          {codCount != null
            ? codCount.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'ยอดโอน',
      dataIndex: 'bankTotal',
      key: 'bankTotal',
      width: 100,
      align: 'center' as const,
      render: (bankTotal: number) => (
        <Typography variant="subtitle2" color="green" textAlign="center">
          {bankTotal != null
            ? bankTotal.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'จำนวนโอน',
      dataIndex: 'bankCount',
      key: 'bankCount',
      width: 95,
      align: 'center' as const,
      render: (bankCount: number) => (
        <Typography variant="subtitle2" color="text.secondary" textAlign="center">
          {bankCount != null
            ? bankCount.toLocaleString('th-TH', { maximumFractionDigits: 2 })
            : 0}
        </Typography>
      ),
    },
    {
      title: 'ต้นทุนสินค้า',
      dataIndex: 'costTotal',
      key: 'costTotal',
      width: 110,
      align: 'center' as const,
      render: (costTotal: number) => (
        <Typography variant="subtitle2" color="red" textAlign="center">
          {user?.isNotAllowSeeCost ? '-' : numberFormat((costTotal || 0) * -1)}
        </Typography>
      ),
    },
    {
      title: 'ค่าใช้จ่าย',
      dataIndex: 'costAmountTotal',
      key: 'costAmountTotal',
      width: 90,
      align: 'center' as const,
      render: () => (
        <Typography variant="subtitle2" color="red" textAlign="center">
          {numberFormat(0)}
        </Typography>
      ),
    },
    {
      title: 'รายรับ',
      key: 'income',
      width: 100,
      align: 'center' as const,
      render: (_: any, row: IByDistributionReportRow) => (
        <Typography variant="subtitle2" color="green" textAlign="center">
          {getIncome(row).toLocaleString('th-TH', {
            maximumFractionDigits: 2,
          })}
        </Typography>
      ),
    },
    {
      title: 'รายจ่าย',
      key: 'expense',
      width: 100,
      align: 'center' as const,
      render: (_: any, row: IByDistributionReportRow) => (
        <Typography variant="subtitle2" color="red" textAlign="center">
          {numberFormat(getExpense(row) * -1)}
        </Typography>
      ),
    },
    {
      title: 'คงเหลือ',
      key: 'balance',
      width: 100,
      align: 'center' as const,
      render: (_: any, row: IByDistributionReportRow) => {
        const amount = getBalance(row)
        return (
          <Typography
            variant="subtitle2"
            color={amount > 0 ? 'green' : 'red'}
            textAlign="center"
          >
            {numberFormat(amount)}
          </Typography>
        )
      },
    },
  ]

  return (
    <Table
      loading={props.isLoading}
      size="middle"
      columns={columns}
      scroll={{ x: columns.reduce((pre, cur) => pre + (cur.width || 0), 0) }}
      dataSource={reportsArray}
      pagination={
        onPageChange
          ? {
            current: page,
            pageSize: perPage,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '20', '50'],
            showTotal: (t, range) =>
              `${range[0]}-${range[1]} จาก ${t} เพจ`,
            onChange: (p, size) =>
              onPageChange(p, size ?? perPage),
          }
          : false
      }
      bordered
      rowKey={(row) => row.distributionObjectId ?? `null-${row.distributionName}`}
      summary={() => (
        <Table.Summary>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <Typography variant="subtitle2" fontWeight="bold">
                รวม
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1}>
              <Typography variant="subtitle2" color="primary" textAlign="center">
                {numberFormat(totalSumOrder)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2}>
              <Typography variant="subtitle2" textAlign="center">
                {numberFormat(totalSumUnit)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3}>
              <Typography variant="subtitle2" color="green" textAlign="center">
                {numberFormat(totalSumCodTotal)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4}>
              <Typography variant="subtitle2" textAlign="center">
                {numberFormat(totalSumCodCount)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5}>
              <Typography variant="subtitle2" color="green" textAlign="center">
                {numberFormat(totalSumBankTotal)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6}>
              <Typography variant="subtitle2" textAlign="center">
                {numberFormat(totalSumBankCount)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7}>
              <Typography variant="subtitle2" color="red" textAlign="center">
                {numberFormat((totalSumCostTotal ?? 0) * -1)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={8}>
              <Typography variant="subtitle2" color="red" textAlign="center">
                {numberFormat(0)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={9}>
              <Typography variant="subtitle2" color="green" textAlign="center">
                {numberFormat(totalSumSubTotal)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={10}>
              <Typography variant="subtitle2" color="red" textAlign="center">
                -{numberFormat(totalSumCost)}
              </Typography>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={11}>
              <Typography
                variant="subtitle2"
                color={totalSumTotalAll > 0 ? 'green' : 'red'}
                textAlign="center"
              >
                {numberFormat(
                  totalSumTotalAll > 0 ? totalSumTotalAll : totalSumTotalAll * -1,
                )}
              </Typography>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  )
}

export default DailySalesByDistributionReportTable
